/**
 * task 产物里 runtime_main 日志的定位与筛选（纯函数，便于 node --test）。
 *
 * SIL 产物目录 ``{taskId}/log/`` 下有 runtime_main 的常规日志，以及
 * ``runtime_main.record_time.log``（每行带 record 时间、无墙钟，供两次运行做对齐对比）。
 * 前端按前缀 ``{taskId}/log/runtime_main`` 列举，有几份就给几个下载入口，
 * 不在前端写死常规日志的文件名。
 */

export interface RuntimeLogFile {
  filename: string;
  path: string;
  url: string;
}

export const RECORD_TIME_LOG_FILENAME = 'runtime_main.record_time.log';

const RUNTIME_LOG_STEM = 'runtime_main';

const runtimeLogDir = (taskId: number): string => `${taskId}/log/`;

/** 列举用的 BOS key 前缀。 */
export const buildRuntimeLogPrefix = (taskId: number): string =>
  `${runtimeLogDir(taskId)}${RUNTIME_LOG_STEM}`;

/**
 * 只保留 ``{taskId}/log/`` 目录下（不含子目录）、有直链的 runtime_main 日志；
 * 按文件名去重，record_time 日志排在最后，其余按文件名排序。
 */
export const selectRuntimeLogFiles = (
  taskId: number,
  files: RuntimeLogFile[],
): RuntimeLogFile[] => {
  const dir = runtimeLogDir(taskId);
  const byName = new Map<string, RuntimeLogFile>();
  for (const f of files) {
    if (!f.url || !f.path.startsWith(dir)) continue;
    const rest = f.path.slice(dir.length);
    if (!rest.startsWith(RUNTIME_LOG_STEM) || rest.includes('/')) continue;
    if (!byName.has(rest)) byName.set(rest, { ...f, filename: rest });
  }
  const rank = (name: string) => (name === RECORD_TIME_LOG_FILENAME ? 1 : 0);
  return [...byName.values()].sort(
    (a, b) => rank(a.filename) - rank(b.filename) || a.filename.localeCompare(b.filename),
  );
};

/** Task 日志弹窗里该日志那一行的标题（后接预签名链接）。 */
export const runtimeLogLabel = (filename: string): string =>
  filename === RECORD_TIME_LOG_FILENAME
    ? '场景时间戳日志'
    : `运行日志（${filename}）`;

/** 场景时间日志的悬停说明；其余日志无说明。 */
export const runtimeLogHint = (filename: string): string | undefined =>
  filename === RECORD_TIME_LOG_FILENAME
    ? '每行时间戳为场景录制时间（非运行时系统时间），同一场景多次运行可直接逐行对比'
    : undefined;
